/**
 * Платная часть метрик публикаций — из рекламного кабинета через Marketing API.
 *
 * Instagram API платную часть по репостам и сохранениям не отдаёт вовсе, а по
 * просмотрам — только суммой с органикой. Поэтому цифры приходят отдельным
 * запросом и подмешиваются к уже собранным строкам статистики.
 */

import { getAuthHeaders } from './auth-headers';
import { SUPABASE_FUNCTIONS_URL } from './config';

export type PaidStats = {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saved: number;
  reach: number;
  spend: number;
  /** Сколько объявлений сложилось в эту строку. */
  ads: number;
};

export type PaidStatsResponse = {
  /** false — у аккаунта разбивка не включена, показывать нечего. */
  enabled: boolean;
  /** Все объявления по shortcode из названия — склейка старого бэкенда. */
  paid: Record<string, PaidStats>;
  /** Ключ — id органического поста из креатива объявления. */
  paid_by_media?: Record<string, PaidStats>;
  /** Объявления без исходного поста в креативе — по shortcode из названия. */
  paid_unlinked?: Record<string, PaidStats>;
};

const EMPTY: PaidStatsResponse = { enabled: false, paid: {} };

/** Из ссылки на пост достаём shortcode: /reel/X/, /reels/X/ и /p/X/. */
export function extractShortcode(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(?:reels?|p|tv)\/([A-Za-z0-9_-]+)/i);
  return m ? m[1] : null;
}

function mergePaid(a: PaidStats, b: PaidStats): PaidStats {
  return {
    views: a.views + b.views,
    likes: a.likes + b.likes,
    comments: a.comments + b.comments,
    shares: a.shares + b.shares,
    saved: a.saved + b.saved,
    // Аудитории объявлений пересекаются — как и на сервере, берём максимум.
    reach: Math.max(a.reach, b.reach),
    spend: a.spend + b.spend,
    ads: a.ads + b.ads,
  };
}

/**
 * Реклама для строки статистики. Основной ключ — id поста: сервер берёт его
 * из креатива объявления. По ссылке в названии склеивались ошибочно — 11 из
 * 308 объявлений были названы ссылкой на соседний пост. Название осталось
 * только для объявлений без исходного поста в креативе. Старый бэкенд
 * paid_by_media не присылает — тогда прежняя склейка по названию.
 */
export function paidForRow(
  response: PaidStatsResponse,
  row: { mediaId: string; permalink?: string | null },
): PaidStats | null {
  const shortcode = extractShortcode(row.permalink);
  if (!response.paid_by_media) {
    return (shortcode && response.paid[shortcode]) || null;
  }
  const byMedia = response.paid_by_media[row.mediaId];
  const byName = shortcode ? response.paid_unlinked?.[shortcode] : undefined;
  if (byMedia && byName) return mergePaid(byMedia, byName);
  return byMedia || byName || null;
}

/**
 * Отсутствие рекламных данных не должно валить сбор статистики: органика
 * важнее, и без платной части таблица просто выглядит как раньше.
 */
export async function fetchPaidStats(params: {
  accountId: string;
  from: Date;
  to: Date;
}): Promise<PaidStatsResponse> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/instagram/ads-paid-stats`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        account_id: params.accountId,
        date_from: params.from.toISOString(),
        date_to: params.to.toISOString(),
      }),
    });

    if (!response.ok) return EMPTY;

    const data = (await response.json()) as PaidStatsResponse;
    return data?.enabled ? data : EMPTY;
  } catch {
    return EMPTY;
  }
}
